import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * AlphaOSContext — estado ligero del Sistema Operativo del Estudiante.
 * Sin librerías de estado global (la casa usa react-query + Context): aquí solo
 * viven dos cosas que NO son de servidor:
 *   1. Modo Examen (Zen): oculta el chrome de la app + temporizador en marcha.
 *   2. Contexto vivo del Alpha Assistant: qué materia/tema/lección mira el alumno
 *      ahora mismo, para que el mini-chat responda de forma nativa.
 */

const AlphaOSContext = createContext(null);

export function AlphaOSProvider({ children }) {
  // --- Modo Examen ---
  const [examEndsAt, setExamEndsAt] = useState(null); // ms epoch o null
  const [remaining, setRemaining] = useState(0);
  const tickRef = useRef(null);

  const startExam = (minutes = 25) => {
    setExamEndsAt(Date.now() + minutes * 60000);
  };
  const endExam = () => setExamEndsAt(null);

  useEffect(() => {
    if (!examEndsAt) {
      setRemaining(0);
      if (tickRef.current) clearInterval(tickRef.current);
      return;
    }
    const tick = () => setRemaining(Math.max(0, Math.round((examEndsAt - Date.now()) / 1000)));
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [examEndsAt]);

  // bloquea el scroll del fondo mientras el Modo Examen está activo
  useEffect(() => {
    document.body.style.overflow = examEndsAt ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [examEndsAt]);

  // --- Contexto del Assistant (lo rellenan las páginas: lección, curso…) ---
  const [assistantCtx, setAssistantCtx] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const value = useMemo(() => ({
    examMode: !!examEndsAt,
    remaining,
    startExam,
    endExam,
    assistantCtx,
    setAssistantCtx,
    assistantOpen,
    setAssistantOpen,
  }), [examEndsAt, remaining, assistantCtx, assistantOpen]);

  return <AlphaOSContext.Provider value={value}>{children}</AlphaOSContext.Provider>;
}

export function useAlphaOS() {
  const ctx = useContext(AlphaOSContext);
  if (!ctx) throw new Error("useAlphaOS debe usarse dentro de <AlphaOSProvider>");
  return ctx;
}
